import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';

import {
  OperatorTheme,
  OperatorThemeToken
} from '../../models/partner-operation';
import {
  isAllowedThemeOrigin,
  parseThemeUrl,
  themeUrlLogLabel
} from '../../functions/theme-url.function';
import { OperatorConfigService } from '../operator-config/operator-config.service';
import { validateOperatorTheme } from './operator-theme-schema';

/**
 * How long to wait for the partner's host before giving up and leaving the
 * platform default in place. Longer than the 3 s we allow our own bucket
 * (OPERATOR_CONFIG_TIMEOUT_MS): this is a third-party origin we don't control.
 */
export const OPERATOR_THEME_TIMEOUT_MS = 5000;

/**
 * Upper bound on the response body, in bytes.
 *
 * This is a PARSE GUARD, not a transfer cap: by the time it runs the body has
 * already been downloaded in full. What it bounds is the cost of JSON.parse and
 * of walking the result in the validator, so a partner URL that serves a
 * multi-megabyte payload cannot be turned into an object graph. A legitimate
 * token file is well under 4 KB.
 *
 * Capping the transfer itself would need a Content-Length pre-check or a
 * streaming read; neither is worth it for a file this small from a host we have
 * already allowlisted.
 */
export const OPERATOR_THEME_MAX_BYTES = 262144;

@Injectable({
  providedIn: 'root'
})
export class OperatorThemeService {

  // Styling only (colors, typography, spacing, radius). Everything else we
  // configure per operator - currency, languages, i18n overrides, Banko - is
  // DME-managed and lives in one file in our own bucket keyed by operator,
  // iframe/operator_configuration.json (OperatorConfigService; OperatorTranslationService
  // reads its translations from there too). Nothing about those belongs here.
  // Only the theme moved to partner hosting.
  //
  // One file per operator, now published by the operator themselves at a URL
  // they hand us as ?themeUrl= - the same single file is passed to pmu-mobile,
  // so the two apps must validate it identically (see operator-theme-schema.ts
  // and design-system/fixtures/operator-theme/).

  /**
   * Explicit whitelist of the CSS custom properties an operator theme may
   * ever set. Never blindly apply every property coming from the JSON.
   */
  private readonly allowedProperties: Array<keyof OperatorTheme> = [
    '--theme-primary',
    '--theme-primary-light',
    '--theme-secondary',
    '--theme-cta-success',
    '--theme-primary-text',
    '--theme-secondary-text',
    '--theme-cta-success-text',
    '--reunion-tab-active-bg',
    '--reunion-tab-active-text',
    '--reunion-tab-active-border',
    '--bet-type-ring',
    '--race-date-nav-bg',
    '--race-date-nav-text',
    '--race-date-nav-border',
    '--partant-formula-idle-bg',
    '--horse-number-badge-bg',
    '--horse-number-badge-text',
    '--betslip-spot-badge-bg',
    '--betslip-spot-badge-text',
    '--rapport-position-badge-bg',
    '--rapport-position-badge-text',
    '--rapport-tied-badge-bg',
    '--rapport-tied-badge-text',
    '--race-status-ended-bg',
    '--race-status-ended-text',
    '--row-stripe-odd-bg',
    '--row-stripe-even-bg',
    '--row-warning-bg',
    '--operator-font-scale',
    '--operator-line-height',
    '--operator-spacing-density',
    '--operator-radius-scale'
  ];

  /**
   * Numeric-scale properties (unitless multipliers) get a range check
   * instead of the hex-color check every other allowed property gets.
   * Ranges mirror design-system/schema/operator-theme.schema.json.
   */
  private readonly numericPropertyRanges: Partial<Record<keyof OperatorTheme, [number, number]>> = {
    '--operator-font-scale': [0.8, 1.2],
    '--operator-line-height': [1.2, 2.0],
    '--operator-spacing-density': [0.8, 1.2],
    '--operator-radius-scale': [0, 2]
  };

  constructor(
    private readonly http: HttpClient,
    private readonly operatorConfigService: OperatorConfigService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
  }

  /**
   * Fetch and apply an operator's design-token file from the URL the partner
   * supplied (`?themeUrl=`), if everything about it checks out.
   *
   * The file used to live in our own bucket at a path we derived from the
   * operator id, vetted by us before upload. It is now hosted by the operator
   * and named by a query param, so three things are checked here that the
   * publish pipeline used to cover:
   *
   *   1. the URL is one we're willing to fetch at all (parseThemeUrl)
   *   2. this operator is allowed to be themed FROM THAT ORIGIN - the
   *      `themeOrigins` allowlist in our own registry (OperatorConfigService)
   *   3. the payload really is a design-token file (validateOperatorTheme),
   *      and claims the same operator id as the query param
   *
   * Check 2 is the authorization decision and check 3 is NOT. The token file
   * declares its own `operator.id`, so a file on any host can claim to be any
   * operator; matching it only proves the file and the link agree. What makes
   * the pair trustworthy is that we named the host in advance.
   *
   * Every failure resolves to `false` with the platform default left in place.
   * Nothing here can throw into the caller.
   *
   * The checks run cheapest-first, and crucially ALL OF THEM RUN BEFORE THE
   * REQUEST. A URL we would not accept is never fetched - which matters beyond
   * the theme itself, because fetching it would tell a host chosen by whoever
   * wrote the link the visitor's IP address.
   */
  applyOperatorTheme(operatorName: string, themeUrl: string): Observable<boolean> {

    const normalizedOperator = this.normalizeOperatorName(operatorName);
    const url = parseThemeUrl(themeUrl);

    if (!normalizedOperator || !url) {
      this.clearOperatorTheme();

      if (normalizedOperator && themeUrl) {
        console.warn(
          '[OperatorTheme] themeUrl rejected (must be an absolute https URL to a public host):',
          themeUrlLogLabel(themeUrl)
        );
      }

      return of(false);
    }

    return this.operatorConfigService.getOperatorThemeOrigins(normalizedOperator).pipe(
      take(1),
      switchMap(origins => {

        if (!isAllowedThemeOrigin(url, origins)) {
          this.clearOperatorTheme();
          console.warn(
            '[OperatorTheme] Operator is not configured to be themed from this origin, ignoring:',
            normalizedOperator, themeUrlLogLabel(themeUrl)
          );

          return of(false);
        }

        // responseType 'text', not get<T>: a partner host answering with an HTML
        // error page or a text/plain content type makes Angular's own JSON
        // parsing throw something opaque, and the raw string is what lets us
        // bound the size before parsing it.
        //
        // withCredentials is left at its default of false, so no cookies travel
        // to the partner's origin - a host named by whoever wrote the link.
        return this.http.get(url.href, { responseType: 'text' }).pipe(
          timeout(OPERATOR_THEME_TIMEOUT_MS),
          map(body => this.applyThemeBody(body, normalizedOperator, url.hostname)),
          catchError(() => {
            this.clearOperatorTheme();
            console.warn('[OperatorTheme] Could not load theme from', url.hostname);

            return of(false);
          })
        );
      })
    );
  }

  /**
   * Turn a fetched response body into an applied theme, or into `false`.
   *
   * Only the hostname is ever logged, never the URL or the payload: a partner's
   * theme URL can carry a signed query token, and the body is their content.
   */
  private applyThemeBody(body: string, normalizedOperator: string, hostname: string): boolean {

    // Real bytes, not body.length: that counts UTF-16 code units, so a payload
    // of non-ASCII text measures well under its actual size.
    if (typeof body !== 'string' || new TextEncoder().encode(body).length > OPERATOR_THEME_MAX_BYTES) {
      this.clearOperatorTheme();
      console.warn('[OperatorTheme] Theme payload too large or not text, ignoring:', hostname);

      return false;
    }

    let token: OperatorThemeToken;

    try {
      token = JSON.parse(body);
    } catch {
      this.clearOperatorTheme();
      console.warn('[OperatorTheme] Theme payload is not valid JSON, ignoring:', hostname);

      return false;
    }

    const { valid, errors, warnings } = validateOperatorTheme(token);

    // Contrast failures are warnings at runtime, errors in the publish CLI - a
    // shade below 4.5:1 must not make a live partner's whole theme disappear.
    warnings.forEach(warning => {
      console.warn('[OperatorTheme] Accessibility warning for', normalizedOperator + ':', warning);
    });

    if (!valid) {
      this.clearOperatorTheme();
      console.warn(
        '[OperatorTheme] Theme does not match the design-token contract, ignoring:',
        normalizedOperator, errors
      );

      return false;
    }

    // A consistency check, not an authorization one: this field is inside the
    // very file being vetted, so it can claim anything. It catches a link
    // pointing at the wrong operator's file on an origin we DO trust; what
    // keeps an untrusted file out at all is the themeOrigins allowlist above.
    if (this.normalizeOperatorName(token.operator?.id) !== normalizedOperator) {
      this.clearOperatorTheme();
      console.warn(
        '[OperatorTheme] Theme declares a different operator than the one requested, ignoring:',
        normalizedOperator
      );

      return false;
    }

    // Clear and re-apply together rather than clearing up front: the fetch
    // ahead of this can take seconds against a third-party host, and clearing
    // early would show the platform defaults in the meantime.
    this.clearOperatorTheme();
    this.applyTheme(this.tokenToRuntimeTheme(token));

    return true;
  }

  /**
   * Remove runtime overrides.
   *
   * The original SCSS values immediately become active again.
   */
  clearOperatorTheme(): void {

    const root = this.document.documentElement;

    this.allowedProperties.forEach(property => {
      root.style.removeProperty(property);
    });
  }

  /**
   * Maps a fetched operator theme token file (schemaVersion "2.0") to the
   * flat OperatorTheme CSS-custom-property shape applyTheme() knows how to
   * apply. Only ever sets a key the operator actually supplied (or, for
   * primaryLight, has a color to fall back to).
   */
  private tokenToRuntimeTheme(token: OperatorThemeToken): OperatorTheme {

    const brand = token?.color?.brand;
    const cta = token?.color?.cta;
    const component = token?.color?.component || {};

    if (!brand || !cta) {
      return {};
    }

    const theme: OperatorTheme = {
      '--theme-primary': brand.primary,
      '--theme-primary-light': brand.primaryLight || brand.primary,
      '--theme-primary-text': brand.primaryText,
      '--theme-secondary': brand.secondary,
      '--theme-secondary-text': brand.secondaryText,
      '--theme-cta-success': cta.success,
      '--theme-cta-success-text': cta.successText
    };

    if (component.reunionTab) {
      const t = component.reunionTab;
      if (t.activeBg) theme['--reunion-tab-active-bg'] = t.activeBg;
      if (t.activeText) theme['--reunion-tab-active-text'] = t.activeText;
      if (t.activeBorder) theme['--reunion-tab-active-border'] = t.activeBorder;
    }

    if (component.raceDateNav) {
      const n = component.raceDateNav;
      if (n.bg) theme['--race-date-nav-bg'] = n.bg;
      if (n.text) theme['--race-date-nav-text'] = n.text;
      if (n.border) theme['--race-date-nav-border'] = n.border;
    }

    if (component.betType?.ring) {
      theme['--bet-type-ring'] = component.betType.ring;
    }

    if (component.partantFormula?.idleBg) {
      theme['--partant-formula-idle-bg'] = component.partantFormula.idleBg;
    }

    if (component.horseNumberBadge) {
      const h = component.horseNumberBadge;
      if (h.bg) theme['--horse-number-badge-bg'] = h.bg;
      if (h.text) theme['--horse-number-badge-text'] = h.text;
    }

    if (component.betslipSpotBadge) {
      const b = component.betslipSpotBadge;
      if (b.bg) theme['--betslip-spot-badge-bg'] = b.bg;
      if (b.text) theme['--betslip-spot-badge-text'] = b.text;
    }

    if (component.rapportPositionBadge) {
      const p = component.rapportPositionBadge;
      if (p.bg) theme['--rapport-position-badge-bg'] = p.bg;
      if (p.text) theme['--rapport-position-badge-text'] = p.text;
    }

    if (component.rapportTiedBadge) {
      const t2 = component.rapportTiedBadge;
      if (t2.bg) theme['--rapport-tied-badge-bg'] = t2.bg;
      if (t2.text) theme['--rapport-tied-badge-text'] = t2.text;
    }

    if (component.raceStatusEnded) {
      const r = component.raceStatusEnded;
      if (r.bg) theme['--race-status-ended-bg'] = r.bg;
      if (r.text) theme['--race-status-ended-text'] = r.text;
    }

    if (component.rowStriping) {
      const s = component.rowStriping;
      if (s.oddBg) theme['--row-stripe-odd-bg'] = s.oddBg;
      if (s.evenBg) theme['--row-stripe-even-bg'] = s.evenBg;
    }

    if (component.rowNonPartant?.bg) {
      theme['--row-warning-bg'] = component.rowNonPartant.bg;
    }

    if (token.typography?.scale !== undefined) {
      theme['--operator-font-scale'] = String(token.typography.scale);
    }
    if (token.typography?.bodyLineHeight !== undefined) {
      theme['--operator-line-height'] = String(token.typography.bodyLineHeight);
    }
    if (token.spacing?.density !== undefined) {
      theme['--operator-spacing-density'] = String(token.spacing.density);
    }
    if (token.radius?.scale !== undefined) {
      theme['--operator-radius-scale'] = String(token.radius.scale);
    }

    return theme;
  }

  private applyTheme(theme: OperatorTheme): void {

    const root = this.document.documentElement;

    this.allowedProperties.forEach(property => {

      const value = theme[property];

      if (!value) {
        return;
      }

      const range = this.numericPropertyRanges[property];

      const isValid = range
        ? this.isValidNumericScale(value, range[0], range[1])
        : this.isValidColor(value);

      if (!isValid) {
        console.warn(
          `[OperatorTheme] Invalid value for ${property}:`,
          value
        );

        return;
      }

      root.style.setProperty(
        property,
        value
      );
    });
  }

  private normalizeOperatorName(operatorName: string | undefined): string {

    return (operatorName || '')
      .trim()
      .toLowerCase();
  }

  /**
   * Deliberately restrictive, and deliberately 6-digit only: that is what the
   * design-token contract says (design-system/schema/operator-theme.schema.json),
   * and the file is now the partner's, so runtime and contract have to agree.
   * validateOperatorTheme() has already enforced this - this stays as the last
   * gate in front of setProperty().
   */
  private isValidColor(value: string): boolean {

    return /^#[0-9a-fA-F]{6}$/.test(value.trim());
  }

  /**
   * Numeric-scale properties are unitless multipliers (e.g. "1.05"),
   * never raw CSS lengths - this is what keeps an operator from being
   * able to inject an arbitrary calc()/unit through this path.
   */
  private isValidNumericScale(value: string, min: number, max: number): boolean {

    if (!/^-?\d+(\.\d+)?$/.test(value.trim())) {
      return false;
    }

    const parsed = Number(value);

    return parsed >= min && parsed <= max;
  }
}
